import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';

import { Button } from '@shared/ui/button';
import DataTable from '@shared/ui/data-table';
import type { Column } from '@shared/ui/data-table';
import { MyModal } from '@shared/ui/my-modal';
import { PushBadge } from '@entities/marketing-push/ui';
import { charLength, formatPushDate } from '@entities/marketing-push/lib';
import type { PushPermissions } from '@entities/marketing-push/lib';
import {
  asList,
  marketingPushApi,
  pushError,
} from '../../../api/services/marketingPush.service';
import type {
  PushNotificationType,
  PushTemplate,
  PushTestDevice,
} from '../../../types/marketingPushInterfaces';

const FIELD_LABEL = 'block text-sm font-medium text-[#2d2c2c] mb-1';
const INPUT = 'h-11 w-full rounded-lg border border-gray-300 px-3 text-sm text-[#2d2c2c]';

/* ========================================================================== */
/* Шаблоны                                                                     */
/* ========================================================================== */

/**
 * Шаблоны здесь только перечисляются, переименовываются и удаляются.
 * Создаются они из формы рассылки кнопкой «Сохранить как шаблон», применяются
 * оттуда же. Отдельный редактор шаблона был бы второй копией формы рассылки
 * со своей валидацией и разошёлся бы с ней при первом изменении контракта.
 */
export function PushTemplatesTab({ permissions }: { permissions: PushPermissions }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PushTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [renaming, setRenaming] = useState<PushTemplate | null>(null);
  const [name, setName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await marketingPushApi.getTemplates();
      setRows(asList(data));
    } catch (loadError) {
      setError(pushError(loadError, 'Не удалось загрузить шаблоны'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRename() {
    if (!renaming) return;
    try {
      await marketingPushApi.saveTemplate({ ...renaming, name: name.trim() });
      setRenaming(null);
      load();
    } catch (renameError) {
      setError(pushError(renameError, 'Не удалось переименовать шаблон'));
    }
  }

  async function handleDelete(template: PushTemplate) {
    if (!window.confirm(`Удалить шаблон «${template.name}»?`)) return;
    try {
      await marketingPushApi.deleteTemplate(template.id);
      load();
    } catch (deleteError) {
      setError(pushError(deleteError, 'Не удалось удалить шаблон'));
    }
  }

  const columns: Column<PushTemplate>[] = [
    {
      key: 'name',
      header: 'Название',
      render: (template) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-[#2d2c2c]">{template.name}</p>
          <p className="truncate text-xs text-gray-400">
            {template.notification_type ?? 'тип не задан'} ·{' '}
            {template.contents.map((content) => content.language).join(', ') || 'без языков'}
          </p>
        </div>
      ),
    },
    {
      key: 'targets',
      header: 'Приложения',
      render: (template) => (
        <div className="flex flex-wrap gap-1">
          {template.targets.length ? (
            template.targets.map((target) => <PushBadge key={target}>{target}</PushBadge>)
          ) : (
            <span className="text-xs text-gray-400">не выбраны</span>
          )}
        </div>
      ),
    },
    {
      key: 'updated',
      header: 'Изменён',
      render: (template) => (
        <span className="text-xs text-[#2d2c2c]">{formatPushDate(template.updated_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      headerClassName: 'w-[1%] whitespace-nowrap',
      stopRowClick: true,
      render: (template) => (
        <div className="flex items-center justify-end gap-2 whitespace-nowrap">
          {permissions.create && (
            <button
              type="button"
              title="Откроет новую рассылку и предложит применить шаблон"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => navigate(`/marketing/push/new?template=${template.id}`)}
            >
              Использовать
            </button>
          )}
          {permissions.manageTemplates && (
            <>
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={() => {
                  setRenaming(template);
                  setName(template.name);
                }}
              >
                Переименовать
              </button>
              <button
                type="button"
                className="text-xs text-red-600 hover:underline"
                onClick={() => handleDelete(template)}
              >
                Удалить
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-xl bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={rows}
          keyAccessor={(template) => template.id}
          loading={loading}
          emptyMessage="Шаблонов пока нет. Шаблон создаётся из формы рассылки кнопкой «Сохранить как шаблон»."
        />
      </div>

      <MyModal
        isOpen={renaming !== null}
        onClose={() => setRenaming(null)}
        title="Переименовать шаблон"
        size="sm"
      >
        <label className={FIELD_LABEL}>Название</label>
        <input
          value={name}
          maxLength={200}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          className={INPUT}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button isWhite onClick={() => setRenaming(null)}>
            Отмена
          </Button>
          <Button disabled={charLength(name.trim()) < 2} onClick={() => void handleRename()}>
            Сохранить
          </Button>
        </div>
      </MyModal>
    </>
  );
}

/* ========================================================================== */
/* Типы уведомлений                                                            */
/* ========================================================================== */

/**
 * Признак «требует согласия» — не косметика: у таких типов аудитория
 * считается только по установкам, давшим согласие на рекламу в приложении.
 * Поэтому включение флага у существующего типа резко сужает его аудиторию,
 * и об этом предупреждает баннер в модалке.
 */
export function PushTypesTab({ permissions }: { permissions: PushPermissions }) {
  const [rows, setRows] = useState<PushNotificationType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<Partial<PushNotificationType> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await marketingPushApi.getNotificationTypes();
      setRows(asList(data));
    } catch (loadError) {
      setError(pushError(loadError, 'Не удалось загрузить типы'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave() {
    if (!editing) return;
    setSaving(true);
    setError('');
    try {
      await marketingPushApi.saveNotificationType(editing);
      setEditing(null);
      load();
    } catch (saveError) {
      setError(pushError(saveError, 'Не удалось сохранить тип'));
    } finally {
      setSaving(false);
    }
  }

  const isNew = Boolean(editing && !editing.id);
  const enablingConsent =
    !isNew &&
    Boolean(editing?.requires_consent) &&
    !rows.find((row) => row.id === editing?.id)?.requires_consent;

  const columns: Column<PushNotificationType>[] = [
    {
      key: 'name',
      header: 'Тип',
      render: (type) => (
        <div>
          <p className="font-medium text-[#2d2c2c]">{type.name}</p>
          <p className="font-mono text-xs text-gray-400">{type.code}</p>
        </div>
      ),
    },
    {
      key: 'consent',
      header: 'Согласие',
      render: (type) =>
        type.requires_consent ? (
          <PushBadge tone="warning">требуется</PushBadge>
        ) : (
          <span className="text-xs text-gray-400">не требуется</span>
        ),
    },
    {
      key: 'active',
      header: 'Состояние',
      render: (type) =>
        type.is_active ? (
          <PushBadge tone="success">активен</PushBadge>
        ) : (
          <PushBadge>отключён</PushBadge>
        ),
    },
    {
      key: 'actions',
      header: '',
      headerClassName: 'w-[1%] whitespace-nowrap',
      stopRowClick: true,
      render: (type) =>
        permissions.manageTypes ? (
          <button
            type="button"
            className="text-xs text-blue-600 hover:underline"
            onClick={() => setEditing({ ...type })}
          >
            Изменить
          </button>
        ) : null,
    },
  ];

  return (
    <>
      {permissions.manageTypes && (
        <div className="mb-4 flex justify-end">
          <Button
            onClick={() =>
              setEditing({
                code: '',
                name: '',
                requires_consent: false,
                is_active: true,
                sort_order: 100,
              })
            }
          >
            <span className="inline-flex items-center gap-1">
              <FiPlus /> Добавить тип
            </span>
          </Button>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-xl bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={rows}
          keyAccessor={(type) => type.id}
          loading={loading}
          emptyMessage="Типов пока нет"
        />
      </div>

      <p className="mt-3 text-sm text-gray-500">
        Отключённый тип нельзя выбрать в новой рассылке, но у отправленных он остаётся —
        история не переписывается.
      </p>

      <MyModal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={isNew ? 'Новый тип уведомления' : 'Изменить тип'}
        size="sm"
      >
        {editing && (
          <>
            <div className="mb-3">
              <label className={FIELD_LABEL}>
                Название <span className="text-red-500">*</span>
              </label>
              <input
                value={editing.name ?? ''}
                maxLength={128}
                onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                className={INPUT}
              />
            </div>

            <div className="mb-3">
              <label className={FIELD_LABEL}>
                Код <span className="text-red-500">*</span>
              </label>
              <input
                value={editing.code ?? ''}
                maxLength={64}
                disabled={!isNew}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    code: event.target.value.toLowerCase().replace(/\s+/g, '_'),
                  })
                }
                className={`${INPUT} font-mono`}
              />
              <p className="mt-1 text-xs text-gray-500">
                {isNew
                  ? 'Латиница и подчёркивания. Уходит в API и в payload уведомления.'
                  : 'Код менять нельзя: он уходит в мобильные приложения.'}
              </p>
            </div>

            <label className="flex cursor-pointer items-start gap-2 py-1">
              <input
                type="checkbox"
                className="mt-1"
                checked={Boolean(editing.requires_consent)}
                onChange={() =>
                  setEditing({ ...editing, requires_consent: !editing.requires_consent })
                }
              />
              <span>
                <span className="text-sm text-[#2d2c2c]">Требует согласия на рекламу</span>
                <span className="block text-xs text-gray-500">
                  Уведомления такого типа получат только те, кто дал согласие в приложении.
                </span>
              </span>
            </label>

            <label className="flex cursor-pointer items-start gap-2 py-1">
              <input
                type="checkbox"
                className="mt-1"
                checked={Boolean(editing.is_active)}
                onChange={() => setEditing({ ...editing, is_active: !editing.is_active })}
              />
              <span>
                <span className="text-sm text-[#2d2c2c]">Активен</span>
                <span className="block text-xs text-gray-500">
                  Неактивный тип не показывается при создании рассылки.
                </span>
              </span>
            </label>

            {enablingConsent && (
              <div className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                <strong className="block">Аудитория сузится</strong>
                После включения признака рассылки этого типа будут уходить только тем, кто
                дал согласие. Согласие нельзя получить задним числом, поэтому у установок,
                перенесённых из старого сервиса, его нет.
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <Button isWhite onClick={() => setEditing(null)}>
                Отмена
              </Button>
              <Button
                disabled={saving || !editing.name?.trim() || !editing.code?.trim()}
                onClick={() => void handleSave()}
              >
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          </>
        )}
      </MyModal>
    </>
  );
}

/* ========================================================================== */
/* Тестовые устройства                                                         */
/* ========================================================================== */

/**
 * Привязка идёт коротким кодом из приложения, а не идентификатором установки.
 * Причина не в удобстве: install_id — секрет, по нему читается история
 * уведомлений установки, и показывать его в ЛК нельзя. Код одноразовый,
 * живёт 15 минут и ничего не раскрывает.
 */
export function PushDevicesTab() {
  const [rows, setRows] = useState<PushTestDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [pairing, setPairing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await marketingPushApi.getTestDevices();
      setRows(asList(data));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handlePair() {
    setError('');
    setNotice('');
    setPairing(true);
    try {
      const { data } = await marketingPushApi.pairTestDevice(code, label || undefined);
      setNotice(`Устройство «${data.label || data.app_target}» привязано.`);
      setCode('');
      setLabel('');
      load();
    } catch (pairError) {
      setError(pushError(pairError, 'Не удалось привязать устройство'));
    } finally {
      setPairing(false);
    }
  }

  async function handleUnpair(device: PushTestDevice) {
    if (!window.confirm('Отвязать устройство?')) return;
    try {
      await marketingPushApi.unpairTestDevice(device.id);
      load();
    } catch (unpairError) {
      setError(pushError(unpairError, 'Не удалось отвязать устройство'));
    }
  }

  const columns: Column<PushTestDevice>[] = [
    {
      key: 'label',
      header: 'Устройство',
      render: (device) => (
        <div>
          <p className="font-medium text-[#2d2c2c]">{device.label || device.app_target}</p>
          <p className="text-xs text-gray-400">
            {device.app_target} · {device.language}
            {device.app_version ? ` · версия ${device.app_version}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'state',
      header: 'Состояние',
      render: (device) =>
        device.is_active ? (
          <PushBadge tone="success">активно</PushBadge>
        ) : (
          <PushBadge tone="warning">уведомления отключены</PushBadge>
        ),
    },
    {
      key: 'seen',
      header: 'Последняя активность',
      render: (device) => (
        <span className="text-xs text-[#2d2c2c]">{formatPushDate(device.last_seen_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      headerClassName: 'w-[1%] whitespace-nowrap',
      stopRowClick: true,
      render: (device) => (
        <button
          type="button"
          className="text-xs text-red-600 hover:underline"
          onClick={() => handleUnpair(device)}
        >
          Отвязать
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">
        <h2 className="mb-2 text-base font-semibold text-[#2d2c2c]">Привязать устройство</h2>
        <ol className="mb-4 list-decimal pl-5 text-sm text-gray-500">
          <li>Откройте приложение на своём телефоне.</li>
          <li>В настройках найдите экран разработчика и нажмите «Получить код».</li>
          <li>Введите код ниже. Он действует 15 минут и только один раз.</li>
        </ol>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}
        {notice && (
          <div className="mb-3 rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-[180px]">
            <label className={FIELD_LABEL}>
              Код <span className="text-red-500">*</span>
            </label>
            <input
              value={code}
              placeholder="K7M2QP"
              maxLength={16}
              // Код набирают руками с экрана телефона: приводим к верхнему
              // регистру сами, чтобы не отказывать из-за раскладки.
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              className={`${INPUT} text-center font-mono tracking-[0.12em]`}
            />
          </div>
          <div className="min-w-[240px] flex-1">
            <label className={FIELD_LABEL}>Название</label>
            <input
              value={label}
              placeholder="Мой Pixel"
              maxLength={100}
              onChange={(event) => setLabel(event.target.value)}
              className={INPUT}
            />
            <p className="mt-1 text-xs text-gray-500">
              Чтобы отличать свои устройства в списке
            </p>
          </div>
          <div className="mb-6">
            <Button
              disabled={pairing || code.trim().length < 4}
              onClick={() => void handlePair()}
            >
              {pairing ? 'Привязываем…' : 'Привязать'}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={rows}
          keyAccessor={(device) => device.id}
          loading={loading}
          emptyMessage="Устройств пока нет. Привяжите телефон, чтобы отправлять тестовые уведомления перед массовой рассылкой."
        />
      </div>
    </>
  );
}
